import {
  ChangeEventHandler,
  memo,
  PropsWithChildren,
  ReactElement,
  useCallback,
} from 'react';
import { CheckIcon } from 'components/icons';
import { EventCollectorProps } from 'hooks/eventCollector/types';
import { HTMLString } from 'types/HTMLString';
import CheckboxEvent from 'hooks/eventCollector/events/CheckboxEvent';
import classes from 'classnames';
import styles from './Checkbox.module.scss';
import Text from '../Typography/Text';
import useEventCollector from 'hooks/eventCollector/useEventCollector';

export type CheckboxProps = {
  touched?: boolean;
  dataTest?: string;
  disabled?: boolean;
  name?: string;
  className?: string;
  classNameInput?: string;
  value: boolean;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  text?: HTMLString | ReactElement;
  textHTML?: HTMLString;
  highlight?: boolean;
  labelClassName?: string;
  errors?: boolean;
};

export default memo(function Checkbox({
  dataTest = 'checkbox',
  name,
  className,
  classNameInput,
  disabled,
  value,
  onChange,
  text,
  textHTML,
  highlight,
  labelClassName,
  errors,
  eventCollector,
}: PropsWithChildren<CheckboxProps> & EventCollectorProps) {
  const { track } = useEventCollector();

  const handleAnalytics = useCallback(
    (checked: boolean) => {
      if (eventCollector?.dontTrack) {
        return;
      }

      track(
        new CheckboxEvent({
          checkboxChecked: checked,
          description:
            eventCollector?.description ?? name ?? 'unnamed_checkbox',
        }),
      );
    },
    [eventCollector?.description, eventCollector?.dontTrack, name, track],
  );

  const handleOnChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      if (onChange) {
        onChange(e);
        handleAnalytics(e.target.checked);
      }
    },
    [onChange, handleAnalytics],
  );

  return (
    <div
      className={classes(
        styles.wrapper,
        value && styles.checked,
        disabled && styles.disabled,
        errors && styles.errors,
        className,
      )}
      data-test={`${dataTest}-wrapper`}
    >
      <div className={styles.checkmark} data-test={`${dataTest}-checkmark`}>
        <input
          type="checkbox"
          name={name}
          data-test={dataTest}
          disabled={disabled}
          checked={value}
          onChange={handleOnChange}
          className={classes(styles.checkbox, classNameInput)}
        />
        <CheckIcon />
      </div>
      {(text || textHTML) && (
        <Text
          level={2}
          className={classes(
            styles['checkbox-label'],
            highlight && styles.highlighted,
            labelClassName,
          )}
          dangerouslySetInnerHTML={textHTML ? { __html: textHTML } : undefined}
          kind="p"
          text={text}
          dataTest={`${dataTest}-label`}
        />
      )}
    </div>
  );
});
